/**
 * <sticky-announcement> — rend la barre d'annonce collante (port du
 * `sticky-announcement` Shrine).
 *
 * `data-sticky-type` :
 *  - `none`         : aucun comportement (par défaut) ;
 *  - `always`       : la barre reste en haut de l'écran ;
 *  - `on-scroll-up` : elle se cache en descendant, réapparaît en remontant.
 *
 * La hauteur de la barre est publiée dans `--ab-sticky-height` sur <html> pour
 * que le header collant d'Horizon se place juste en dessous.
 */
class StickyAnnouncement extends HTMLElement {
  /** @type {HTMLElement | null} */
  #section = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #lastScroll = 0;
  #hidden = false;
  #type = 'none';

  connectedCallback() {
    this.#type = this.dataset.stickyType ?? 'none';
    if (this.#type === 'none') return;

    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    this.#section = section;
    section.classList.add('ab-section--sticky');

    this.#publishHeight();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
      this.#resizeObserver.observe(this.firstElementChild ?? this);
    }

    if (this.#type === 'on-scroll-up') {
      this.#lastScroll = window.scrollY;
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    window.removeEventListener('scroll', this.#onScroll);
    document.documentElement.style.removeProperty('--ab-sticky-height');
  }

  /** Hauteur courante de la barre (0 quand elle est masquée). */
  #publishHeight() {
    const height = this.#hidden ? 0 : (this.#section?.getBoundingClientRect().height ?? 0);
    document.documentElement.style.setProperty('--ab-sticky-height', `${Math.round(height)}px`);
  }

  #onScroll = () => {
    const current = window.scrollY;
    const goingDown = current > this.#lastScroll;
    const hidden = goingDown && current > 80;

    this.#lastScroll = current;

    if (hidden === this.#hidden) return;

    this.#hidden = hidden;
    this.#section?.classList.toggle('ab-section--hidden', hidden);
    this.#publishHeight();
  };
}

if (!customElements.get('sticky-announcement')) {
  customElements.define('sticky-announcement', StickyAnnouncement);
}